import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Copy, Send } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Contact({ settings }) {
  const email = settings?.contact_email || "operations@portbridgeglobal.com";
  const phone = settings?.contact_phone || "+1 (555) 013-4200";
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/inquiries", form);
      toast.success(data.message);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#0066FF]/25 bg-[#070F1E] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-[#00F0FF]";

  return (
    <section id="contact" className="relative bg-[#070F1E] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow>Contact</Eyebrow>
          <h2 className="max-w-2xl font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Talk to the team behind the network.
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div className="space-y-5">
            <Reveal delay={0.05}>
              <button
                data-testid="contact-email-card"
                onClick={() => copy(email, "Email")}
                className="group flex w-full items-center gap-5 rounded-2xl border border-[#0066FF]/20 bg-[#0B172A] p-6 text-left transition-all hover:border-[#00F0FF]/50"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0066FF]/15">
                  <Mail className="h-5 w-5 text-[#00F0FF]" />
                </span>
                <span className="flex-1">
                  <span className="block font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Email</span>
                  <span className="mt-1 block text-base font-semibold text-white">{email}</span>
                </span>
                <Copy className="h-4 w-4 text-slate-600 transition-colors group-hover:text-[#00F0FF]" />
              </button>
            </Reveal>

            <Reveal delay={0.1}>
              <button
                data-testid="contact-phone-card"
                onClick={() => copy(phone, "Phone")}
                className="group flex w-full items-center gap-5 rounded-2xl border border-[#0066FF]/20 bg-[#0B172A] p-6 text-left transition-all hover:border-[#00F0FF]/50"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0066FF]/15">
                  <Phone className="h-5 w-5 text-[#00F0FF]" />
                </span>
                <span className="flex-1">
                  <span className="block font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Phone</span>
                  <span className="mt-1 block text-base font-semibold text-white">{phone}</span>
                </span>
                <Copy className="h-4 w-4 text-slate-600 transition-colors group-hover:text-[#00F0FF]" />
              </button>
            </Reveal>

            <Reveal delay={0.15}>
              <p className="px-2 text-sm leading-relaxed text-slate-500">
                Carrier onboarding questions, load program details and partnership inquiries are
                answered by our operations desk, seven days a week.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-3xl border border-[#0066FF]/20 bg-[#0B172A] p-8">
              <h3 className="font-display text-lg font-bold text-white">Send an inquiry</h3>
              <div className="mt-6 space-y-4">
                <div>
                  <label className="mb-1.5 block font-mono text-xs uppercase tracking-[0.15em] text-slate-500">Name</label>
                  <input
                    data-testid="contact-form-name-input"
                    className={inputCls}
                    placeholder="Your full name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block font-mono text-xs uppercase tracking-[0.15em] text-slate-500">Email</label>
                    <input
                      data-testid="contact-form-email-input"
                      type="email"
                      className={inputCls}
                      placeholder="you@company.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block font-mono text-xs uppercase tracking-[0.15em] text-slate-500">Company</label>
                    <input
                      data-testid="contact-form-company-input"
                      className={inputCls}
                      placeholder="Company (optional)"
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block font-mono text-xs uppercase tracking-[0.15em] text-slate-500">Message</label>
                  <textarea
                    data-testid="contact-form-message-input"
                    rows={4}
                    className={`${inputCls} resize-none`}
                    placeholder="How can we help?"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                </div>
                <button
                  data-testid="contact-form-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0066FF] py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#0052cc] hover:shadow-[0_0_35px_rgba(0,102,255,0.45)] disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  {sending ? "Sending..." : "Send Inquiry"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
